import { Box, List, ListItem, ListItemIcon, ListItemText, Stack, StackProps, styled, Typography,  useTheme  } from "@mui/material";
import { format } from "date-fns";
import { it, enUS } from "date-fns/locale";
import { t } from "i18next";
import { Book, ChevronRight, Clock8, FileBadge, FileText, GraduationCap, PencilRuler, Star, UserRoundCheck } from "lucide-react";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import type { Course, LearningActivity, LearningObject, Level, ReferencedLearningActivity, Root, Stakeholder, StatusLO } from "types";
import { ListItemNavBar } from "./NavigationSidebar.styles";
import { SideBarBoxRestyle } from "./SidebarBox.component";
import defaultImage from "../../assets/profile.png";
import { LearningActivityModal } from "../../components/LearningActiviyModal/LearningActivityModal.component";
import { useRatingQuery } from "../../queries";
import { useNavigate } from "../../services";
import { useAuthStore } from "../../stores";
import { useLanguageStore } from "../../stores/languageStore";
import { getLearningObjectType } from "../../utils/general";
import { WidgetSkeleton } from "../WidgetSkeleton/WidgetSkeleton.component";

//PencilRuler


export function NavigationSidebarRestyle({
  learningObject,
  related,
  attachedFiles,
  activitiesWithEditionsRelated,
  cousesAndActivityNumberLabel,
  isLoading=false,
  elementsToShow
}:{
  learningObject:LearningActivity | Course | Root,
  related:LearningObject [],
  attachedFiles: number,
  activitiesWithEditionsRelated:ReferencedLearningActivity[],
  cousesAndActivityNumberLabel:string,
  reviews?: number,
  isLoading?:boolean,
  elementsToShow:string[]
}){
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const smartConfiguration = useAuthStore(state => state.smartConfiguration);
  const ratingMax = smartConfiguration?.ratingMax;

  const learningObjectType = getLearningObjectType(learningObject.learningObjectTypology);
  const {
    data: ratingStatistic
  } = useRatingQuery({
    enabled: false,
    learningObjectId: learningObject.id ?? "",
    learningObjectTypology: learningObjectType
  });

  const averageRating = ratingStatistic?.objectStatistics.averageRating || 0;

  let numAttachedFiles = attachedFiles;

  const certificateEarned = learningObject.status === "C" && !!learningObject.certificates?.some((c) => c.isEnable);
  const hasCertificate = (learningObject.certificates?.length ?? 0) > 0 || !!learningObject.certificate;
  const attachedCertificate = hasCertificate
    ? (certificateEarned ? t("certificate_attached") : t("certificate_available_on_completion"))
    : "";

  const ratingScoreLabel = !smartConfiguration?.funcRating
    ? ""
    : averageRating
      ? averageRating.toString() + "/" + ratingMax
      : t("no_rating");

  function handleNumAttachedFiles() {
    if(numAttachedFiles === 0){
      return "";
    }

    let label =  `${t("attached_file_other",{ count: numAttachedFiles })}`;

    if(numAttachedFiles === 1){
      label = `${t("attached_file_one",{ count: numAttachedFiles })}`;
    }

    return label;
  }

  const isEnrolledStatus = (status?: StatusLO) => status === "E" || status === "P" || status === "C";

  // L'endpoint descendants-classrooms restituisce enrollType (es. "TOP"),
  const isTopDownEnrollType = (enrollType?: string) => !!enrollType && enrollType.includes("TOP");

  // Le aule Top-Down/Top-Mandatory a cui l'utente non è iscritto restano nascoste in spalla:
  // vengono mostrate solo se l'utente è già iscritto, oppure se ammettono auto-iscrizione.
  const isActivityVisibleInSidebar = (activity: ReferencedLearningActivity) => {
    const enrolled = isEnrolledStatus(activity.status) ||
      (activity.editions || []).some(edition => isEnrolledStatus(edition.status));
    return enrolled || !isTopDownEnrollType(activity.enrollType);
  };

  const visibleActivitiesWithEditionsRelated = activitiesWithEditionsRelated.filter(isActivityVisibleInSidebar);

  const isEditionSectionVisible = visibleActivitiesWithEditionsRelated.length > 0;

  let configDettaglio :{id:number, icon:React.ReactElement, label:string|number }[] = [
    {
      id:1,
      icon: <Book color={ theme.customColors.exploreCardText }/>,
      label: cousesAndActivityNumberLabel
    },
    {
      id:2,
      icon:<FileText color={ theme.customColors.exploreCardText }/>,
      label: handleNumAttachedFiles()
    },
    {
      id:3,
      icon: <Star color={ theme.customColors.exploreCardText }/>,
      label: ratingScoreLabel
    },
    {
      id:4,
      icon:<FileBadge color={ theme.customColors.exploreCardText }/>,
      label: attachedCertificate
    }
  ];

  function typeActualObject(LOtype: Level) {
    if(LOtype !== "PATH" && LOtype !== "COURSE" && LOtype !== "DA_VIRTUAL_CLASS" ){
      return t("learning_activity");
    }
    if(LOtype === "PATH") return t("package");
    if(LOtype === "COURSE") return t("course");
    if(LOtype === "DA_VIRTUAL_CLASS") return t("virtual_location");
  }

  if(isLoading){
    return (
      <StackSidebarStyled >
        <WidgetSkeleton />
      </StackSidebarStyled>

    );}

  function showStakeHolders() {
    // shows stakeholders sectgion only if there is at least one stakeholder with visStakeholder true
    // and if the element is included in the elementsToShow array
    if(elementsToShow.includes("STAKEHOLDERS") &&
      learningObject?.stakeholders &&
      learningObject?.stakeholders?.length > 0 &&
      learningObject?.stakeholders?.some(el => el.visStakeholder)){
      return true;
    }
    return false;
  }

  return (
    <StackSidebarStyled
      id="navigation-sidebar"
      data-testid="navigation-sidebar-root"
      role="complementary"
      aria-label={ t("related_content") }
    >
      {  configDettaglio.some(el => el.label !== 0 && el.label !== "") &&

        <SideBarBoxRestyle
          title={ `${t("details")} ${typeActualObject(learningObject.learningObjectTypology)}` }
        >
          <List
            disablePadding={ true }
            sx={ {
              padding: 0,
              gap: "0.5rem",
              display: "flex",
              flexDirection: "column"
            } }
          >
            { configDettaglio.filter(el => el.label !== 0 && el.label !== "")
              .map((el,i) => {
                let labelToShow = el.label;
                let ariaLabelToShow = el.label;
                if(labelToShow?.toString().includes("|")){
                  ariaLabelToShow = labelToShow?.toString().replace(/\|/g, t("and"));
                }
                return(
                  <ListItem
                    key={ i }
                    sx={ {
                      padding: "0"
                    } }
                  >
                    <ListItemIcon
                      sx={ {
                        color: theme.customColors.exploreCardText,
                        marginRight: "0.5rem" ,
                        minWidth: 0
                      } }>
                      { React.cloneElement(el.icon, {
                        style: {
                          width: "1rem",
                          height: "1rem"
                        }
                      }) }
                    </ListItemIcon>
                    <ListItemText
                      tabIndex={ 0 }
                      aria-label={ `${ariaLabelToShow}` }
                      primary={ labelToShow }
                      sx={ {
                        marginY: 0,
                        paddingTop: "0.125rem"
                      } }
                      primaryTypographyProps={ {
                        fontSize: "0.75rem",
                        fontWeight: 400
                      } }
                    />
                  </ListItem>
                );
              }) }
          </List>
        </SideBarBoxRestyle>
      }
      { isEditionSectionVisible &&
        <SideBarBoxRestyle
          title={ t("agenda_tab_live") }
          titleButtonHeader={ t("go_to_agenda") }
          actionButtonHeader={ ()=> {
            navigate("/agenda");
          } }
        >
          <List
            disablePadding={ true }
            sx={ {
              padding: 0,
              gap: "0.5rem",
              display: "flex",
              flexDirection: "column"
            } }
          >
            { /* eslint-disable-next-line @typescript-eslint/no-explicit-any*/ }
            { visibleActivitiesWithEditionsRelated
              .map((activity: ReferencedLearningActivity, index: number) => {
                return (
                  <EventiLiveComponent key={ index } el={ activity }  ></EventiLiveComponent>
                );
              }) }
          </List>
        </SideBarBoxRestyle>
      }
      { /*terzo container */ }
      { showStakeHolders() && (
        <SideBarBoxRestyle title={ "Stakeholders" }>
          <List
            disablePadding={ true }
            sx={ {
              padding: 0,
              gap: "1rem",
              display: "flex",
              flexDirection: "column"
            } }
          >
            { /* eslint-disable-next-line @typescript-eslint/no-explicit-any*/ }
            { (learningObject.stakeholders || [])
              .filter(el => el.visStakeholder)
              .map((el, index: number) => (
                <Stakeholders key={ index } stakeholder={ el } />
              )) }

          </List>
        </SideBarBoxRestyle>
      ) }
      { /*quarto container */ }
      { elementsToShow.includes("RELATED") &&
        <SideBarBoxRestyle title={ t("you_interested") }>
          <List
            disablePadding={ true }
            sx={ {
              padding: 0,
              gap: "0.5rem",
              display: "flex",
              flexDirection: "column"
            } }
          >
            { related.map((elRelated: LearningObject, index: number) => {
              return (
                <SuggestedList
                  key={ index }
                  el={ elRelated }
                />
              );
            }) }
          </List>
        </SideBarBoxRestyle>
      }
    </StackSidebarStyled>
  );
}



export const CourseNavigationSidebar = styled("div")<StackProps & {registered?: boolean}>(({ theme, registered = false }) => ({
  "& > div": {
    display: "flex",
    gap: "0.5rem"
  },
  "&:before": {
    background: registered ? theme.customColors.borderSecondaryComplete : theme.customColors.systemSecondary01,
    borderRadius: "50rem",
    content: "''",
    marginRight: "0.5rem",
    width: "2px"
  },
  ":hover": {
    cursor: "pointer"
  },
  borderRadius: "0.5rem",
  display: "flex",
  flexDirection: "row",
  gap: "0.5rem",
  padding: "0.5rem 0.5rem"
}));



function ImageSquare({ src, alt,size,name }: { src?: string; alt: string,size:string,name?: string }) {
  const theme = useTheme();
  return (
    <Box
      sx={ {
        width: size,
        height: size,
        position: "relative", // 👈 key change
        borderRadius: "0.5rem",
        overflow: "hidden",
        backgroundColor: theme.customColors.backgroundSecondary
      } }
    >
      { src ? (
        <img
          src={ src || defaultImage }
          alt={ alt }
          loading="lazy"
          style={ {
            width: "100%",
            height: "100%",
            objectFit: "fill",
            display: "block" // 👈 IMPORTANT
          } }
        />
      ) : (
        <Typography
          sx={ {
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: 700,
            fontSize: `calc(${size} / 2.5)`, // 👈 dynamic font
            color: "#333"
          } }
        >
          { name
            ?.split(" ")
            .map(i => i.charAt(0))
            .join("")
            .toUpperCase() }
        </Typography>
      ) }
    </Box>
  );
}

const EventiLiveComponent = ({ el }: { el: ReferencedLearningActivity }) => {
  const theme = useTheme();
  const lang = useLanguageStore(state => state.language);
  const [open, setOpen] = useState(false);

  const capitalizeMonth = (dateStr: string): string => {
    const [day, month, year] = dateStr.split(" ");
    return `${day} ${month.charAt(0).toUpperCase() + month.slice(1)} ${year}`;
  };

  const formatDate = (date: [number, number, number], lang: string): string => {
    const [year, month, day] = date;
    const locale = lang === "it" ? it : enUS;
    const formatted = format(new Date(year, month - 1, day), "d MMMM yyyy", { locale });
    return capitalizeMonth(formatted);
  };

  const isSameDate = (start: [number, number, number], end: [number, number, number]): boolean =>
    start[0] === end[0] && start[1] === end[1] && start[2] === end[2];


  let userIsSubscribedToAnEdition = false;
  let editionWithEnrollment = null;
  if(el.editions && el.editions.length > 0){
    userIsSubscribedToAnEdition = el.editions.some(ed => ed?.status === "E");
    editionWithEnrollment = el.editions.find(ed => ed?.status === "E");
  }

  let icon = <UserRoundCheck size={ "1rem" } color={ theme.customColors.exploreCardText }/>;
  let label = "";

  if(userIsSubscribedToAnEdition && editionWithEnrollment){
    const startDateLabel = formatDate(editionWithEnrollment.startDate, lang?.id||"it");
    const endDateLabel = formatDate(editionWithEnrollment.endDate, lang?.id||"it");
    const dateLabel = isSameDate(editionWithEnrollment.startDate, editionWithEnrollment.endDate)
      ? startDateLabel
      : t("from_to", { start: startDateLabel, end: endDateLabel });
    label = t("enrolled") + " | " + dateLabel;
  }

  if(el.editions && el.editions.length > 0 && !userIsSubscribedToAnEdition ){
    icon = <PencilRuler size={ "1rem" } color={ theme.customColors.exploreCardText }/>;
    if(el.editions.length === 1){
      label = `${el.editions.length} ${t("edition_name")}`;
    }else{
      label = `${el.editions?.length || 0} ${t("editions")}`;
    }
  }

  return (
    <ListItemNavBar
      id={ `sidebar-live-event-${el.id}` }
      data-testid="sidebar-live-event"
      disablePadding
      tabIndex={ 0 }
      aria-label={ el.title }
      onKeyDown={ (e: React.KeyboardEvent<HTMLElement>) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          setOpen(true);
        }
      } }
    >
      <CourseNavigationSidebar
        tabIndex={ -1 }
        aria-hidden={ true }
        sx={ { width: "100%" } }
        onClick={ () => setOpen(true) }
        registered={ editionWithEnrollment ? true : false }
      >
        <Box
          sx={ {
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            width:"100%"
          } }>
          <Box sx={ {
            margin:"0.5rem",
            maxWidth: "15.3rem"
          } }>
            <Box>
              <Typography
                color={ theme.customColors.exploreCardText }
                fontSize="0.875rem"
                fontWeight={ 400 }
                lineHeight="1.09375rem"
              >
                { el.title }
              </Typography>
            </Box>

            <Box sx={ { display:"flex",gap: "0.25rem",marginTop:"0.5rem"  } }>
              { /*Da sostiture la condizione con if è un edizione  */ }
              { icon }
              <Typography
                fontSize="0.75rem"
                fontWeight={ 400 }
                color={ theme.customColors.exploreCardText }
              >{ label }</Typography>
            </Box>
          </Box>
          <Box  sx={ {
            display: "flex",
            alignItems: "center",
            justifyContent: "center", // horizontally center inside its box
            height: "100%",           // full height of parent
            flexShrink: 0
          } }>

            <LearningActivityModal open={ open } setOpen={ setOpen } activity={ el }
              customButton={
                <ChevronRight size="1.25rem" color={ theme.customColors.textPrimary } />
              }/>
          </Box>
        </Box>
      </CourseNavigationSidebar>
    </ListItemNavBar>
  );

};

const SuggestedList = ({ el }: { el: LearningObject }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const viewDetails = () => {
    navigate.navigateToDetail({
      id:el.id,
      isForYou: false,
      learningObjectTypology:el.learningObjectTypology,
      courseId: el.parentId ?? undefined,
      rootId: el.grandParentId ?? undefined
    });
  };
  const labelDuration = `${t("duration")}: ${el.duration}`;
  const labelAccessibilty = `${el.title} ${el.duration ? labelDuration:""} `;
  return (
    <ListItemNavBar
      id={ `sidebar-related-${el.id}` }
      data-testid="sidebar-related-item"
      disablePadding
      tabIndex={ 0 }
      aria-label={ labelAccessibilty }
      onKeyDown={ (e: React.KeyboardEvent<HTMLElement>) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          viewDetails();
        }
      } }
      onClick={ () => viewDetails() }
    >
      <Box
        aria-hidden={ true }
        sx={ { width: "100%" } }
      >
        <Box
          sx={ {
            display: "flex",
            alignItems: "center",
            width: "100%",
            borderRadius: "0.5rem",
            gap: "0.5rem",
            padding: "0.25rem 0.5rem"
          } }
        >
          { /* IMAGE */ }
          <Box sx={ { flexShrink: 0 } }>
            <ImageSquare size="3rem" alt="" src={ el.coverPublicURL } />
          </Box>

          { /* TEXT */ }
          <Box
            sx={ {
              flex: 1,
              minWidth: 0
            } }
          >
            <Typography
              color={ theme.customColors.exploreCardText }
              fontSize="0.75rem"
              textAlign="left"
            >
              { el.title.length > 30
                ? el.title.slice(0, 30) + "..."
                : el.title }
            </Typography>

            <Typography
              color={ theme.customColors.exploreCardText }
              fontSize="0.75rem"
              textAlign="left"
            >
              { el.shortDescription.length > 30
                ? el.shortDescription.slice(0, 30) + "..."
                : el.shortDescription }
            </Typography>

            { el.duration && (
              <Box sx={ { display: "flex", gap: "0.25rem", mt: "0.5rem" } }>
                <Clock8
                  size="0.75rem"
                  color={ theme.customColors.textDisabledAlternative }
                />
                <Typography
                  fontSize="0.75rem"
                  fontWeight={ 400 }
                  color={ theme.customColors.textDisabledAlternative }
                >
                  { el.duration }
                </Typography>
              </Box>
            ) }
          </Box>

          { /* ARROW */ }
          <Box
            sx={ {
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor:"pointer"
            } }
          >
            <ChevronRight size="1.25rem" color={ theme.customColors.textPrimary } />
          </Box>
        </Box>
      </Box>
    </ListItemNavBar>
  );
};


export const Stakeholders = ({ stakeholder }:{stakeholder: Stakeholder}) => {
  const theme = useTheme();
  return (
    <ListItem
      id={ `sidebar-stakeholder-${stakeholder.userId}` }
      data-testid="sidebar-stakeholder-item"
      disablePadding
      tabIndex={ 0 }
      aria-label={ `${stakeholder.name}. ${t("role")}:${stakeholder.type}` }
      onKeyDown={ (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
        }
      } }
    >
      <Box
        //TODO : verificare che non ci siano azioni per gli stakeholders
        aria-hidden={ true }
        onClick={ () => console.log("click") } >
        <Box
          sx={ {
            display: "flex",
            justifyContent: "space-between",
            width:"100%",
            borderRadius: "0.5rem",
            flexDirection: "row",
            gap: "1rem"
          } }>
          <ImageSquare size="4rem" alt="" src={ stakeholder.imageProfile } name={ stakeholder.name }></ImageSquare>
          <Box sx={ {
            margin:"0.5rem 0.5rem 0.5rem 0",
            maxWidth: "15.3rem",
            gap: "0.5rem",
            wordBreak: "break-word" } }>
            <Box sx={ { gap:"0.5rem",display: "flex",
              flexDirection: "column" } }>
              <Typography
                color={ theme.customColors.exploreCardText }
                fontSize="0.875rem"
                fontWeight={ 400 }
                lineHeight="1.09375rem"
              >
                { stakeholder.name }
              </Typography>
              <Box sx={ { display:"flex",gap: "0.25rem" } }>
                { stakeholder?.iconVisible && <GraduationCap size={ 16 }/> }
                <Typography
                  color={ theme.customColors.exploreCardText }
                  fontSize="0.875rem"
                  fontWeight={ 400 }
                  lineHeight="1.09375rem"
                >
                  { stakeholder.type }
                </Typography>
              </Box>

            </Box>
          </Box>
        </Box>
      </Box>
    </ListItem>
  );
};

const StackSidebarStyled = styled(Stack)(({ theme }) => ({
  "&:hover": {
    borderColor: theme.customColors.borderAccent
  },
  background: theme.customColors.backgroundPrimary,
  border: `0.0625rem solid ${theme.customColors.border}`,
  borderRadius: "1.125rem",
  color: theme.customColors.exploreCardText,
  display: "flex",
  flexDirection: "column",
  maxWidth: "24rem",
  padding: "1.5rem",
  transition: "all 0.2s ease",
  width: "24rem",
  "& > *:not(:last-child)": {
    borderBottom: `1px solid ${theme.customColors.border}`,
    paddingBottom: "1.5rem",
    marginBottom: "1.5rem"
  },
  [theme.breakpoints.down("sm")]: {
    maxWidth: "100%",
    minWidth: "15rem",
    width: "100%"
  }
}));

